// Taşeron portalı — route ağacı.
//
// Auth ekranları (login / firma seçimi / parola sıfırlama / guest-activation)
// oturum AÇIKKEN panele yönlenir (`RedirectIfAuthenticated`); panel oturum YOKSA
// login'e yönlenir (`RequireAuth`). Firma seçimi (`/select-workspace`) transit
// token'ı router state üzerinden alır (store'a yazılmaz) — state yoksa login'e döner.

import { RedirectIfAuthenticated, RequireAuth, openPersonnelState } from '@isgportal/shared';
import { Navigate, createBrowserRouter, useParams } from 'react-router-dom';
import { SupplierContractorGate } from './components/SupplierContractorGate';
import { SupplierShellGate } from './components/SupplierShellGate';
import { AuthLayout } from './layouts/AuthLayout';
import { DashboardPage } from './pages/DashboardPage';
import { DocumentAnalysisInboxPage } from './pages/DocumentAnalysisInboxPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { GuestActivatePage } from './pages/GuestActivatePage';
import { LoginPage } from './pages/LoginPage';
import { MobileAppPage } from './pages/MobileAppPage';
import { PermitDetailPage } from './pages/PermitDetailPage';
import { PermitsListPage } from './pages/PermitsListPage';
import { PersonnelPage } from './pages/PersonnelPage';
import { ProfilePage } from './pages/ProfilePage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { SelectWorkspacePage } from './pages/SelectWorkspacePage';
import { VehiclesPage } from './pages/VehiclesPage';

// GL5 — profil sayfası kalktı: eski `/personnel/:userUuid` ve `/personnel/:userUuid/documents`
// adresleri (yer imleri, e-posta bağlantıları) listeye, o kişinin çekmecesi açık gelecek biçimde
// yönlenir. İstek URL'de değil router STATE'indedir; liste tüketip temizler (yenile/Geri → düz liste).
function PersonnelLegacyRedirect() {
  const { userUuid = '' } = useParams();
  return <Navigate to="/personnel" replace state={openPersonnelState(userUuid)} />;
}

export const router = createBrowserRouter([
  {
    element: (
      <RedirectIfAuthenticated redirectTo="/">
        <AuthLayout />
      </RedirectIfAuthenticated>
    ),
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/select-workspace', element: <SelectWorkspacePage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      { path: '/guest/activate', element: <GuestActivatePage /> },
    ],
  },
  {
    // Giriş/aktivasyon sonrası panel-vs-app kapısı (D3): panel yetkisi YOK (Personeli) →
    // tüm kabuk yerine tam-ekran indirme (`AppDownloadOnly`); panel yetkisi VAR (Yetkili) veya
    // servis geçici erişilemez (unavailable) → normal portal (`AppLayout` + Outlet).
    element: (
      <RequireAuth redirectTo="/login">
        <SupplierShellGate />
      </RequireAuth>
    ),
    children: [
      { path: '/', element: <DashboardPage /> },
      // Kişisel profil — `profile` yetki modülü AllAuthorized (grant gerekmez); Gate YOK.
      { path: '/profile', element: <ProfilePage /> },
      { path: '/documents', element: <DocumentsPage /> },
      // Mobil Uygulama sayfası (§26 app-store linkleri) — panel-gate DIŞI (herhangi
      // authenticated okur). NAV'daki "Mobil Uygulama" girişinden açılır.
      { path: '/mobile-app', element: <MobileAppPage /> },
      // Personel yönetimi (liste + belgeler, §15.2 yol B) — yalnız `supplier-contractor`
      // (Yetkili). Yalnız `supplier-self` (Personeli) bu alt-ağacı GÖRMEZ (SupplierContractorGate
      // → 403 forbidden). İki katmanlı authz UI ayrımı.
      {
        path: '/personnel',
        element: <SupplierContractorGate />,
        children: [
          { index: true, element: <PersonnelPage /> },
          { path: ':userUuid', element: <PersonnelLegacyRedirect /> },
          { path: ':userUuid/documents', element: <PersonnelLegacyRedirect /> },
        ],
      },
      // Araçlar (GL #25) — kayıt + belgeler (§23.2); yalnız `supplier-contractor` (Yetkili).
      {
        path: '/vehicles',
        element: <SupplierContractorGate />,
        children: [{ index: true, element: <VehiclesPage /> }],
      },
      // İş İzinleri (liste + detay, §21) — yalnız `supplier-contractor` (Yetkili). İzin
      // bilgileri salt-okunur (İSG uzmanı yönetir); Yetkili kendi personelini atar/çıkarır +
      // readiness görür. Personeli (yalnız `supplier-self`) bu alt-ağacı GÖRMEZ (Gate → 403).
      {
        path: '/permits',
        element: <SupplierContractorGate />,
        children: [
          { index: true, element: <PermitsListPage /> },
          { path: ':permitUuid', element: <PermitDetailPage /> },
        ],
      },
      // Belge Analizi gelen kutusu (doc-check, api-contract §20; plan S25 C13) — tek ekranda
      // TÜM bekleyen analizler. `/document-analysis` VE `/document-analysis/:uuid` AYNI
      // bileşene açılır; `:uuid` yalnız hangi analizin seçili geldiğini belirler — mevcut derin
      // bağlantılar (tray, `PendingAnalysesStrip`) kırılmaz. Eskiden ikinci bir giriş noktası
      // (index) yoktu (S19-D12) — artık VAR, çünkü index kendisi gelen kutusudur, ayrı bir
      // yükleme başlatma ekranı DEĞİL. Yalnız `supplier-contractor` (Yetkili),
      // `/personnel` ile AYNI `[ModuleAction("supplier-contractor", …)]` altında.
      {
        path: '/document-analysis',
        element: <SupplierContractorGate />,
        children: [
          { index: true, element: <DocumentAnalysisInboxPage /> },
          { path: ':uuid', element: <DocumentAnalysisInboxPage /> },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
